import { supabase } from '@/lib/supabase';
import { LEAD_PRICE } from '@/data/constants';
import { currentUserIsTraining, TRAINING_PAYMENT_BLOCKED, TRAINING_PAYMENT_MESSAGE } from '@/lib/trainingAccount';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * $29 SINGLE LEAD — client side of the one-off purchase.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * TWO DIFFERENT PRODUCTS — do not collapse them:
 *
 *   SINGLE $29   the contractor picks a project FIRST, pays $29 for THAT
 *                project, and that exact project unlocks the moment the
 *                payment succeeds. Contact details appear immediately and the
 *                lead is in My Leads forever.
 *
 *   MEMBERSHIP   Basic $99 / Pro $199 / Premium $499 buy a POOL of credits.
 *                The contractor browses and spends one credit per lead with
 *                Unlock Lead, whenever they choose.
 *
 * Every call below is a thin wrapper over the `single-lead-purchase` edge
 * function. Nothing here is trusted: the buyer is resolved from the bearer
 * token server-side, the selected listing is stored ON THE ORDER at intent
 * time, and the unlock happens in ONE transaction keyed by the payment intent
 * id — so a double tap, a second tab or a replayed webhook can never double
 * charge, double credit or create a second purchase.
 */



export const SINGLE_LEAD_PRICE = LEAD_PRICE;

/** Publishable key + connected account for Stripe Elements. */
export const STRIPE_PUBLISHABLE_KEY = 'pk_live_9eBOACiG83hSt0n4y7Kg9Exo';
export const STRIPE_ACCOUNT_ID = 'acct_1UDYNuB5WwGm6PiX';

export interface LeadPreflight {
  ok: boolean;
  found: boolean;
  soldOut: boolean;
  alreadyOwned: boolean;
  slotsTaken: number;
  slotsMax: number;
  extraCredits: number;
  /** An unfinished payment for this same lead that can be resumed. */
  pendingPaymentIntentId: string | null;
  error?: string;
}

/**
 * SERVER-SIDE VERIFICATION GATE CODES — single source of truth for the browser.
 * Returned as `error` / `reason` by single-lead-purchase (intent + settle) and by
 * mkt_claim_lead (credit unlock). Both come from the DB function mkt_purchase_gate:
 *   email_verification_required  mkt_profiles.email_verification_state = 'unverified'
 *                                ('verified' and 'legacy_unverified' pass)
 *   phone_verification_required  only when mkt_purchase_gate_config.require_phone_verification
 *                                is ON (default OFF)
 */
export const VERIFICATION_GATE_CODES = ['email_verification_required', 'phone_verification_required'] as const;
export type VerificationGateCode = (typeof VERIFICATION_GATE_CODES)[number];
export const isVerificationGate = (code?: string | null): code is VerificationGateCode =>
  !!code && (VERIFICATION_GATE_CODES as readonly string[]).includes(code);

/**
 * PAYMENT RISK BLOCK CODES (2026-10-07 fraud controls, DB mkt_payment_start_gate / mkt_record_payment_attempt).
 * Returned by single-lead-purchase (route / intent / report_failure) and checkout-link (memberships).
 * Nothing is ever charged when one of these is returned.
 */
// 2026-10-07b: payment_identity_denied = identity on mkt_payment_denylist (Admin → Security → Payment denylist).
export const RISK_BLOCK_CODES = ['payment_temporarily_locked', 'payment_rate_limited', 'payment_identity_denied'] as const;
export const isRiskBlock = (code?: string | null) => !!code && (RISK_BLOCK_CODES as readonly string[]).includes(code);

export interface LeadIntent {
  clientSecret: string | null;
  paymentIntentId: string | null;
  soldOut: boolean;
  alreadyOwned: boolean;
  error?: string;
  message?: string;
  /** Present when the inline intent failed and the server issued a working hosted checkout instead. */
  hostedUrl?: string | null;
}

export interface LeadSettlement {
  ok: boolean;
  /**
   * TRUE when the $29 payment unlocked the EXACT lead that was selected before
   * checkout. That is the normal outcome: the selection is stored on the order
   * at intent time and spent on that listing inside one transaction.
   * FALSE only when the project filled up (or closed) during the payment — the
   * credit is then banked and can be spent on another open project.
   */
  unlocked: boolean;
  /** The payment added a credit to the balance (false on an idempotent replay). */
  credited: boolean;
  /** How many credits this payment put in the balance. Always 1 or 0. */
  creditsGranted: number;
  reason: string;
  purchaseId: string | null;
  /** The listing that was unlocked (or that the payment was bound to). */
  listingId: string | null;
  /** Slug of that listing, so the UI can jump straight to the unlocked lead. */
  slug: string | null;
  /** One-off credits bought at $29 each. */
  extraCredits: number;
  /** Credits included with an active membership. */
  planCredits: number;
  /** Everything the contractor can spend right now. */
  balance: number;
  slotsTaken: number;
  slotsMax: number;
  error?: string;
}



/** Coarse device label stored with the order, so mobile issues are traceable. */
export const deviceLabel = (): string => {
  if (typeof navigator === 'undefined') return 'server';
  const ua = navigator.userAgent || '';
  const mobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const browser = /CriOS/i.test(ua)
    ? 'chrome-ios'
    : /FxiOS/i.test(ua)
    ? 'firefox-ios'
    : /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)
    ? 'safari'
    : /Edg/i.test(ua)
    ? 'edge'
    : /Chrome|Chromium/i.test(ua)
    ? 'chrome'
    : 'other';
  return `${mobile ? 'mobile' : 'desktop'}/${browser}`;
};

/** True for touch-first viewports — used to avoid popup-blocked new tabs. */
export const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  const narrow = window.matchMedia?.('(max-width: 767px)')?.matches ?? window.innerWidth < 768;
  const touch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
  return narrow || touch;
};

/**
 * RESUME KEY. The selected lead + payment intent are remembered locally so a
 * customer who is interrupted (bank app switch, backgrounded Safari tab, dead
 * battery, accidental reload) comes back to the SAME lead and we settle the
 * same payment instead of asking them to find the lead again or pay twice.
 */
const RESUME_KEY = 'gg_single_lead_intent';

export interface ResumeTicket {
  listingId: string;
  slug: string;
  paymentIntentId: string;
  at: number;
}

export const rememberIntent = (ticket: Omit<ResumeTicket, 'at'>) => {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify({ ...ticket, at: Date.now() }));
  } catch {
    /* private mode — resume is a convenience, never a requirement */
  }
};

export const readIntent = (listingId?: string): ResumeTicket | null => {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as ResumeTicket;
    if (!t?.paymentIntentId) return null;
    // Stale tickets (> 6h) are ignored so we never settle something ancient.
    if (Date.now() - (t.at || 0) > 6 * 60 * 60 * 1000) return null;
    if (listingId && t.listingId !== listingId) return null;
    return t;
  } catch {
    return null;
  }
};

export const clearIntent = () => {
  try {
    localStorage.removeItem(RESUME_KEY);
  } catch {
    /* ignore */
  }
};

const MONEY_ACTIONS = new Set(['route', 'intent']);

const call = async (body: Record<string, unknown>) => {
  // TEST/TRAINING accounts never reach a real card form or hosted checkout.
  if (MONEY_ACTIONS.has(String(body.action)) && (await currentUserIsTraining())) {
    const es = body.language === 'es';
    return {
      error: TRAINING_PAYMENT_BLOCKED,
      message: es ? TRAINING_PAYMENT_MESSAGE.es : TRAINING_PAYMENT_MESSAGE.en,
    } as Record<string, unknown>;
  }
  const { data, error } = await supabase.functions.invoke('single-lead-purchase', { body });
  if (error && !data) return { error: 'network' } as Record<string, unknown>;
  return (data || { error: 'error' }) as Record<string, unknown>;
};

/** Re-check role, capacity and entitlement BEFORE showing a card form. */
export const preflightLead = async (listingId: string): Promise<LeadPreflight> => {
  const d = await call({ action: 'preflight', listingId });
  return {
    ok: d.ok === true,
    found: d.found !== false,
    soldOut: d.soldOut === true,
    alreadyOwned: d.alreadyOwned === true,
    slotsTaken: Number(d.slotsTaken || 0),
    slotsMax: Number(d.slotsMax || 6),
    extraCredits: Number(d.extraCredits || 0),
    pendingPaymentIntentId: (d.pendingPaymentIntentId as string) || null,
    error: (d.error as string) || undefined,
  };
};

/**
 * CHECKOUT ROUTING (2026-09-29 root-cause fix).
 *
 * The SERVER decides which checkout can actually take money BEFORE any form is
 * shown. The browser no longer assumes the inline card form works just because
 * a Stripe account id is configured — that assumption is what sent buyers into a
 * form whose account had charges disabled, and then to a dead end.
 *
 *   inline   card form on the page (connected account can charge)
 *   hosted   secure hosted $29 checkout — the buyer is redirected automatically
 *   contact  nothing can take money right now (admins were alerted)
 */
export type LeadRouteMode = 'inline' | 'hosted' | 'contact';
export interface LeadRoute {
  mode: LeadRouteMode | null;
  hostedUrl: string | null;
  sessionId: string | null;
  reason: string;
  soldOut: boolean;
  alreadyOwned: boolean;
  error?: string;
  message?: string;
}

export const routeLead = async (listingId: string, language: string): Promise<LeadRoute> => {
  const d = await call({ action: 'route', listingId, language, device: deviceLabel() });
  const mode = d.mode === 'inline' || d.mode === 'hosted' || d.mode === 'contact' ? (d.mode as LeadRouteMode) : null;
  return {
    mode,
    hostedUrl: (d.hostedUrl as string) || null,
    sessionId: (d.sessionId as string) || null,
    reason: (d.reason as string) || '',
    soldOut: d.soldOut === true,
    alreadyOwned: d.alreadyOwned === true,
    error: (d.error as string) || undefined,
    message: (d.message as string) || undefined,
  };
};

/** Tell the server the buyer came back from the hosted checkout (payment-complete page). */
export const markHostedReturn = async () => {
  try {
    await call({ action: 'returned' });
  } catch {
    /* monitoring only */
  }
};

/** The buyer has waited on the return page with no credit — alert admins immediately. */
export const reportReturnTimeout = async (plan: string, listing: string) => {
  try {
    await supabase.functions.invoke('purchase-health', { body: { action: 'return_timeout', plan, listing } });
  } catch {
    /* monitoring only */
  }
};

/** Create (or reuse) the $29 PaymentIntent for a specific lead. */
export const createLeadIntent = async (listingId: string, language: string): Promise<LeadIntent> => {
  const d = await call({ action: 'intent', listingId, language, device: deviceLabel() });
  return {
    clientSecret: (d.clientSecret as string) || null,
    paymentIntentId: (d.paymentIntentId as string) || null,
    soldOut: d.soldOut === true,
    alreadyOwned: d.alreadyOwned === true,
    error: (d.error as string) || undefined,
    message: (d.message as string) || undefined,
    hostedUrl: (d.hostedUrl as string) || null,
  };
};

/**
 * Settle a succeeded payment.
 *
 * BUSINESS RULE: a $29 payment UNLOCKS THE EXACT LEAD that was selected before
 * checkout. The selection is stored on the order at intent time, and settlement
 * mints one credit and spends it on that listing in ONE transaction. Only when
 * the project has filled up mid-payment does the credit stay banked
 * (`unlocked:false`) so the contractor can spend it elsewhere.
 *
 * Memberships are the other product: they buy a POOL of credits which the
 * contractor spends with Unlock Lead whenever they choose.
 *
 * SAFE TO CALL REPEATEDLY: the database keys the grant on the payment intent
 * id under a row lock, so retries, double taps and a second tab all return the
 * SAME purchase and never issue a second credit or a second unlock.
 */
export const settleLeadPayment = async (
  paymentIntentId: string,
  listingId?: string,
  language = 'en'
): Promise<LeadSettlement> => {
  const d = await call({ action: 'settle', paymentIntentId, listingId, language });
  const extra = Number(d.extraCredits || 0);
  const plan = Number(d.planCredits || 0);
  return {
    ok: d.ok === true,
    unlocked: d.unlocked === true,
    credited: d.credited === true,
    creditsGranted: Number(d.creditsGranted || 0),
    reason: (d.reason as string) || '',
    purchaseId: (d.purchaseId as string) || null,
    listingId: (d.listingId as string) || listingId || null,
    slug: (d.slug as string) || null,
    extraCredits: extra,
    planCredits: plan,
    balance: Number(d.balance ?? extra + plan),
    slotsTaken: Number(d.slotsTaken || 0),
    slotsMax: Number(d.slotsMax || 6),
    error: (d.error as string) || undefined,
  };
};

/**
 * Where did a paid order end up? Used after a 503 / timeout / closed tab / other device. Settlement is finished
 * server-side by the checkout call OR by the background reconciler (every 5 min) — the customer never re-pays.
 */
export const leadPaymentStatus = async (paymentIntentId: string): Promise<LeadSettlement & { pending: boolean }> => {
  const d = await call({ action: 'status', paymentIntentId });
  const extra = Number(d.extraCredits || 0);
  const plan = Number(d.planCredits || 0);
  return {
    ok: d.ok === true,
    pending: d.pending === true || d.error === 'network',
    unlocked: d.unlocked === true,
    credited: d.credited === true,
    creditsGranted: d.credited === true ? 1 : 0,
    reason: (d.reason as string) || '',
    purchaseId: (d.purchaseId as string) || null,
    listingId: (d.listingId as string) || null,
    slug: (d.slug as string) || null,
    extraCredits: extra,
    planCredits: plan,
    balance: Number(d.balance ?? extra + plan),
    slotsTaken: 0,
    slotsMax: Number(d.slotsMax || 3),
  };
};

/** True when a paid $29 was converted into a replacement credit because the lead sold out / closed. */
export const isConvertedToCredit = (s: Pick<LeadSettlement, 'ok' | 'unlocked' | 'reason'>) =>
  s.ok && !s.unlocked && /credit_banked|sold_out|unavailable/.test(s.reason || '');

/**
 * Report a declined / failed card confirmation. The SERVER re-reads the PaymentIntent from the
 * provider (the browser's codes are only a fallback), records it, and applies velocity /
 * card-testing lockouts. Returns locked=true when further attempts are now paused.
 */
export const reportLeadFailure = async (
  paymentIntentId: string,
  code?: string,
  declineCode?: string,
  language = 'en'
): Promise<{ locked: boolean; message?: string }> => {
  try {
    const d = await call({ action: 'report_failure', paymentIntentId, code, declineCode, language });
    return { locked: d.locked === true, message: (d.message as string) || undefined };
  } catch {
    return { locked: false };
  }
};

